import { useEffect, useRef, useState } from "react";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import {
  InstanceOrientation,
  MODALITY,
  MorphologyOrientation,
  SPINESIM_MODALITIES,
} from "../../../../classes/enums/MedicalImagesEnum";
import IconButton from "../../../../components/iconButton/IconButton";
import ImportArea from "../../../../components/importArea/ImportArea";
import NavigationButtons from "../../../../components/navigationButtons/NavigationButtons";
import { usePatientInstancesByModality } from "../../../../hooks/queries/usePatientInstancesByModality";
import { usePatientMorphologyImages } from "../../../../hooks/usePatientMorphologyImages";
import FileImport from "../../../common/uploadModal/fileImport/FileImport";

import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import {
  formatDateAsMonthYear,
  normalizeToYYYYMMDD,
} from "../../../../classes/utils/DateUtils";
import ClinicalHistoryThumbnail from "../../../../components/clinicalHistoryThumbnail";
import { MedicalImageThumbnail } from "../../../../components/medicalImageThumbnail/MedicalImageThumbnail";
import PainAssessmentThumbnail from "../../../../components/painAssessmentThumbnail/PainAssessmentThumbnail";
import { PosturalImageThumbnail } from "../../../../components/posturalImageThumbnail/PosturalImageThumbnail";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useRouteState } from "../../../../hooks/useRouteState";
import {
  addDigitalTwin,
  updateDigitalTwin,
} from "../../../../services/DigitalTwinService";
import PainAssesmentModal from "../../../common/painAssesment/PainAssesmentModal";
import ClinicalHistoryModal from "../../../patients/components/clinicalHistory/ClinicalHistoryModal";
import "./digitalTwinDataInputs.css";

// region Config
const coreConfig = t => [
  {
    key: SPINESIM_MODALITIES.DX,
    title: "DX/CR",
    subtitle: t("238"),
    importComment: t("239"),
  },
  {
    key: SPINESIM_MODALITIES.CT,
    title: t("240"),
    subtitle: t("241"),
    importComment: t("242"),
  },
  {
    key: SPINESIM_MODALITIES.MR,
    title: t("243"),
    subtitle: t("241"),
    importComment: t("244"),
  },
];

const refineConfig = t => [
  {
    key: SPINESIM_MODALITIES.POSTURE,
    title: t("245"),
    subtitle: t("246"),
    importComment: t("247"),
  },
  {
    key: SPINESIM_MODALITIES.PAIN,
    title: t("248"),
    subtitle: t("249"),
    importComment: t("250"),
  },
  {
    key: SPINESIM_MODALITIES.CLINICAL_HISTORY,
    title: t("321"),
    subtitle: t("322"),
    importComment: t("323"),
  },
  // {
  //   key: SPINESIM_MODALITIES.QUALITY,
  //   title: "Quality of Life Indicators",
  //   subtitle: "Select up to 1 survey result",
  //   importComment: "Start new assessment",
  // },
];

const minimumRequirementsConfig = {
  enabled: true,
  requirements: {
    DX: {
      count: 0,
      required: false,
    },
    CT: {
      count: 0,
      required: false,
    },
    MRI: {
      count: 0,
      required: false,
    },
    Posture: {
      count: 0,
      required: false,
    },
    PAIN: {
      count: 0,
      required: false,
    },
    QUALITY: {
      count: 0,
      required: false,
    },
    CLINICAL_HISTORY: {
      count: 0,
      required: false,
    },
  },
};

// endregion

//region UTILS
const normalizeOrientation = orientation => {
  if (!orientation) return "";

  const normalized = orientation.toLowerCase();

  // Group all coronal variations together
  if (normalized.includes(InstanceOrientation.Coronal)) {
    return InstanceOrientation.Coronal;
  }

  // Group all sagittal variations together
  if (normalized.includes(InstanceOrientation.Sagittal)) {
    return InstanceOrientation.Sagittal;
  }

  // Group all axial variations together
  if (normalized.includes("axial")) {
    return "axial";
  }

  return normalized;
};

const upsertSelectedItem = (prevSelectedItems, modality, currentItem) => {
  if (!currentItem) {
    return prevSelectedItems;
  }

  if (modality === MODALITY.DX) {
    const currentNormalizedOrientation = normalizeOrientation(
      currentItem.orientation
    );

    const itemWithSameOrientation = prevSelectedItems[modality].find(
      item =>
        normalizeOrientation(item.orientation) === currentNormalizedOrientation
    );

    if (itemWithSameOrientation) {
      return {
        ...prevSelectedItems,
        [modality]: prevSelectedItems[modality].map(item =>
          normalizeOrientation(item.orientation) === currentNormalizedOrientation
            ? currentItem
            : item
        ),
      };
    }

    return {
      ...prevSelectedItems,
      [modality]: [...prevSelectedItems[modality], currentItem],
    };
  }

  if (
    [
      SPINESIM_MODALITIES.CT,
      SPINESIM_MODALITIES.MR,
      SPINESIM_MODALITIES.PAIN,
      SPINESIM_MODALITIES.QUALITY,
      SPINESIM_MODALITIES.CLINICAL_HISTORY,
    ].includes(modality)
  ) {
    return {
      ...prevSelectedItems,
      [modality]: [currentItem],
    };
  }

  if (modality === SPINESIM_MODALITIES.POSTURE) {
    const currentOrientation =
      currentItem.orientation || currentItem.position || "";

    if (!currentOrientation) {
      if (prevSelectedItems[modality].length < 4) {
        return {
          ...prevSelectedItems,
          [modality]: [...prevSelectedItems[modality], currentItem],
        };
      }
      return prevSelectedItems;
    }

    const itemWithSameOrientation = prevSelectedItems[modality].find(
      item => (item.orientation || item.position) === currentOrientation
    );

    if (itemWithSameOrientation) {
      return {
        ...prevSelectedItems,
        [modality]: prevSelectedItems[modality].map(item =>
          (item.orientation || item.position) === currentOrientation
            ? currentItem
            : item
        ),
      };
    }

    if (prevSelectedItems[modality].length < 4) {
      return {
        ...prevSelectedItems,
        [modality]: [...prevSelectedItems[modality], currentItem],
      };
    }
  }

  return prevSelectedItems;
};
//endregion

//region Sub-components
const DigitalTwinDataInputsStepsHandle = ({
  step,
  isSelected,
  onSelectStep = () => {},
}) => (
  <div
    className={`DigitalTwinDataInput-marker ${isSelected ? "selected" : ""}`}
    onClick={() => onSelectStep(step)}
  />
);

const DigitalTwinDataInputsSteps = ({
  step,
  isSelected,
  onSelectStep = () => {},
  title,
  subtitle,
  children,
}) => (
  <div
    className={`DigitalTwinDataInput-step ${isSelected ? "selected" : ""}`}
    onClick={() => onSelectStep(step)}
  >
    <div
      className={`DigitalTwinDataInput-step-title ${
        isSelected ? "selected" : ""
      }`}
    >
      {title}
    </div>
    {isSelected && subtitle && (
      <div className="DigitalTwinDataInput-step-subtitle">{subtitle}</div>
    )}
    <div className="DigitalTwinDataInput-step-children">
      {isSelected && children}
    </div>
  </div>
);

const StepSection = ({ title, subtitle, children }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const onMouseDown = e => {
      isDown = true;
      el.classList.add("active");
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      e.preventDefault();
    };
    const onMouseMove = e => {
      if (!isDown) return;
      const x = e.pageX - el.offsetLeft;
      const walk = x - startX;
      el.scrollLeft = scrollLeft - walk;
    };
    const onMouseUpOrLeave = () => {
      isDown = false;
      el.classList.remove("active");
    };

    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("mousemove", onMouseMove);
    el.addEventListener("mouseup", onMouseUpOrLeave);
    el.addEventListener("mouseleave", onMouseUpOrLeave);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("mouseup", onMouseUpOrLeave);
      el.removeEventListener("mouseleave", onMouseUpOrLeave);
    };
  }, []);

  return (
    <div className="DigitalTwinDataInput-step-section">
      <div className="DigitalTwinDataInput-step-section-title App-text-large">
        {title}
      </div>
      <div className="DigitalTwinDataInput-step-section-subtitle App-text-medium">
        {subtitle}
      </div>
      <div
        ref={scrollRef}
        className="DigitalTwinDataInput-step-section-children"
      >
        {children}
      </div>
    </div>
  );
};

// endregion

//region Main Component
const DigitalTwinDataInputs = ({
  onGoBack,
  onGenerateTwin,
  selectedStep,
  setSelectedStep,
  selectedItems,
  setSelectedItems,
  requirementsConfig = minimumRequirementsConfig,
  isEditMode = false,
  editingTwin = null,
  lockedSelections = {},
}) => {
  const { t } = useTranslation();
  const { params } = useRouteState();
  const { instancesByModality, refetch } = usePatientInstancesByModality();
  const {
    imagesWithUrls: posturalImages,
    imagesWithUrlsNoBg: posturalImagesNoBg,
  } = usePatientMorphologyImages();
  const { showPopup } = useAppContext();
  const {
    assessmentsByDate: painAssessments,
    clinicalHistories,
    refreshPatientContext,
    fetchPatientImages,
  } = usePatientContext();
  const { userPreferences } = useUser();
  const [pendingImportSelection, setPendingImportSelection] = useState(null);

  // Validate minimum requirements
  const validateRequirements = () => {
    if (isEditMode) {
      // In edit mode, check if any NEW selections were made
      return Object.keys(selectedItems).some(modality => {
        const currentItems = selectedItems[modality] || [];
        const lockedItems = lockedSelections[modality] || [];
        return currentItems.length > lockedItems.length;
      });
    }

    if (!requirementsConfig.enabled) return true;

    // Require at least one medical image (DX, CT, or MRI)
    return [
      SPINESIM_MODALITIES.DX,
      SPINESIM_MODALITIES.CT,
      SPINESIM_MODALITIES.MR,
    ].some(modality => (selectedItems[modality] || []).length > 0);
  };

  // region Callbacks
  const handleSelectThumbnail = (modality, itemId) => {
    const isLocked = lockedSelections[modality]?.includes(itemId);

    setSelectedItems(prevSelectedItems => {
      let currentItem;
      if (modality === SPINESIM_MODALITIES.POSTURE) {
        currentItem = [...posturalImages, ...posturalImagesNoBg].find(
          img => img.url === itemId
        );
      } else if (modality === SPINESIM_MODALITIES.PAIN) {
        // Handle pain assessment selection
        currentItem = painAssessments.find(
          assessment => assessment._id === itemId
        );
      } else if (modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
        // Handle clinical history selection
        currentItem = clinicalHistories?.find(
          history => history._id === itemId
        );
      } else {
        currentItem = instancesByModality[modality]?.find(
          instance => instance._id === itemId
        );
      }

      if (!currentItem) {
        return prevSelectedItems;
      }

      // Check if the item is already selected
      const isSelected = prevSelectedItems[modality].some(item => {
        if (modality === SPINESIM_MODALITIES.POSTURE) {
          return item.url === itemId;
        } else if (modality === SPINESIM_MODALITIES.PAIN) {
          return item._id === itemId;
        } else if (modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
          return item._id === itemId;
        }
        return item._id === itemId;
      });

      // If already selected, remove it (unless locked in edit mode)
      if (isSelected) {
        // Prevent deselection if locked
        if (isEditMode && isLocked) {
          return prevSelectedItems;
        }

        return {
          ...prevSelectedItems,
          [modality]: prevSelectedItems[modality].filter(item => {
            if (modality === SPINESIM_MODALITIES.POSTURE) {
              return item.url !== itemId;
            } else if (modality === SPINESIM_MODALITIES.PAIN) {
              return item._id !== itemId;
            } else if (modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
              return item._id !== itemId;
            }
            return item._id !== itemId;
          }),
        };
      }

      return upsertSelectedItem(prevSelectedItems, modality, currentItem);
    });
  };

  useEffect(() => {
    if (!pendingImportSelection) {
      return;
    }

    const allPosturalImages = [...posturalImages, ...posturalImagesNoBg];
    const resolvedSelections = [];

    const pushNewMedicalItems = modality => {
      const expectedCount = pendingImportSelection.expectedCounts[modality] || 0;
      if (!expectedCount) {
        return;
      }

      const previousIds = pendingImportSelection.previousIdsByModality[modality];
      const newItems = (instancesByModality[modality] || [])
        .filter(instance => !previousIds.has(instance._id))
        .slice(0, expectedCount);

      newItems.forEach(item => {
        resolvedSelections.push({ modality, item });
      });
    };

    pushNewMedicalItems(SPINESIM_MODALITIES.DX);
    pushNewMedicalItems(SPINESIM_MODALITIES.CT);
    pushNewMedicalItems(SPINESIM_MODALITIES.MR);

    const expectedPostureCount =
      pendingImportSelection.expectedCounts[SPINESIM_MODALITIES.POSTURE] || 0;
    if (expectedPostureCount) {
      const newPostureItems = allPosturalImages
        .filter(
          image =>
            !pendingImportSelection.previousPostureTitles.has(image.title)
        )
        .slice(0, expectedPostureCount);

      newPostureItems.forEach(item => {
        resolvedSelections.push({
          modality: SPINESIM_MODALITIES.POSTURE,
          item,
        });
      });
    }

    if (!resolvedSelections.length) {
      return;
    }

    setSelectedItems(prevSelectedItems =>
      resolvedSelections.reduce(
        (nextSelectedItems, { modality, item }) =>
          upsertSelectedItem(nextSelectedItems, modality, item),
        prevSelectedItems
      )
    );
    setPendingImportSelection(null);
  }, [
    instancesByModality,
    pendingImportSelection,
    posturalImages,
    posturalImagesNoBg,
    setSelectedItems,
  ]);

  const isItemSelected = (modality, itemId) => {
    if (!selectedItems || !selectedItems[modality]) {
      return false;
    }

    return selectedItems[modality].some(item => {
      if (modality === SPINESIM_MODALITIES.POSTURE) {
        return item.url === itemId;
      } else if (modality === SPINESIM_MODALITIES.PAIN) {
        return item._id === itemId;
      } else if (modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
        return item._id === itemId;
      }
      return item._id === itemId;
    });
  };

  const stepData = [
    {
      // add translation for "Build the Core (Medical Images)" and subtitle
      title: t("260"),
      subtitle: t("261"),
      sections: coreConfig(t).map(({ key, title, subtitle, importComment }) => (
        <StepSection key={key} title={title} subtitle={subtitle}>
          <div className="DigitalTwinDataInput-step-section-content">
            {instancesByModality[key]?.map(instance => (
              <div
                key={instance._id}
                className="DigitalTwinDataInput-instance-preview"
              >
                <MedicalImageThumbnail
                  instance={instance}
                  isSelected={isItemSelected(key, instance._id)}
                  onClick={() => handleSelectThumbnail(key, instance._id)}
                />
              </div>
            ))}

            <ImportArea
              comment={importComment}
              modality={key}
              onClick={() => handleImportAreaClick(key)}
            />
          </div>
        </StepSection>
      )),
    },
    {
      // add translation for "Refine the Twin (Posture and Pain)" and subtitle
      title: t("262"),
      subtitle: t("263"),
      sections: refineConfig(t).map(
        ({ key, title, subtitle, importComment }) => {
          if (key === SPINESIM_MODALITIES.POSTURE) {
            const allPosturalImages = [
              ...posturalImages,
              ...posturalImagesNoBg,
            ];
            return (
              <StepSection key={key} title={title} subtitle={subtitle}>
                <div className="DigitalTwinDataInput-step-section-content">
                  {allPosturalImages.map(img => (
                    <div
                      key={img.url}
                      className="DigitalTwinDataInput-instance-preview"
                    >
                      <PosturalImageThumbnail
                        key={img.url}
                        image={img}
                        isSelected={isItemSelected(key, img.url)}
                        onClick={() => handleSelectThumbnail(key, img.url)}
                      />
                    </div>
                  ))}

                  <ImportArea
                    comment={importComment}
                    modality={key}
                    onClick={() => handleImportAreaClick(key)}
                  />
                </div>
              </StepSection>
            );
          } else if (key === SPINESIM_MODALITIES.PAIN) {
            return (
              <StepSection key={key} title={title} subtitle={subtitle}>
                <div className="DigitalTwinDataInput-step-section-content">
                  {painAssessments?.map(assessment => (
                    <div
                      key={assessment._id}
                      className="DigitalTwinDataInput-instance-preview"
                    >
                      <PainAssessmentThumbnail
                        assessment={assessment}
                        isSelected={isItemSelected(key, assessment._id)}
                        onClick={() =>
                          handleSelectThumbnail(key, assessment._id)
                        }
                      />
                    </div>
                  ))}

                  <ImportArea
                    comment={importComment}
                    modality={key}
                    onClick={() => handleImportAreaClick(key)}
                  />
                </div>
              </StepSection>
            );
          } else if (key === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
            return (
              <StepSection key={key} title={title} subtitle={subtitle}>
                <div className="DigitalTwinDataInput-step-section-content">
                  {clinicalHistories?.map(history => (
                    <div
                      key={history._id}
                      className="DigitalTwinDataInput-instance-preview"
                    >
                      <ClinicalHistoryThumbnail
                        history={history}
                        isSelected={isItemSelected(key, history._id)}
                        onClick={() => handleSelectThumbnail(key, history._id)}
                      />
                    </div>
                  ))}

                  <ImportArea
                    comment={importComment}
                    modality={key}
                    onClick={() => handleImportAreaClick(key)}
                  />
                </div>
              </StepSection>
            );
          }

          return null;
        }
      ),
    },
  ];

  const handleNextStep = () => {
    if (selectedStep < stepData.length - 1) {
      setSelectedStep(selectedStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (selectedStep > 0) {
      setSelectedStep(selectedStep - 1);
    }
  };

  // Extract date range calculation to avoid duplication
  const calculateDateRange = selectedItems => {
    const flatElements = Object.values(selectedItems).flat();

    // Get oldest and newest dates from the items - NORMALIZE ALL TO YYYYMMDD
    const allDates = flatElements
      .map(item => {
        const dateValue = item.date || item.creation_date;
        if (!dateValue) return null;

        const normalizedDate = normalizeToYYYYMMDD(dateValue);
        return normalizedDate;
      })
      .filter(date => date !== null && date !== "" && date !== "NaNNaNNaN");

    if (allDates.length === 0) {
      return { oldestDate: null, newestDate: null };
    }

    // Now all dates are in YYYYMMDD format, string comparison works correctly
    const oldestDate = allDates.reduce((a, b) => (a < b ? a : b));
    const newestDate = allDates.reduce((a, b) => (a > b ? a : b));

    // Validate the normalized dates
    if (
      !oldestDate ||
      !newestDate ||
      oldestDate === "" ||
      newestDate === "" ||
      oldestDate === "NaNNaNNaN" ||
      newestDate === "NaNNaNNaN"
    ) {
      return { oldestDate: null, newestDate: null };
    }

    return { oldestDate, newestDate };
  };

  // Generate dynamic title based on selected items
  const getDynamicTitle = () => {
    if (isEditMode && editingTwin) {
      // In edit mode, show "Edit Digital Twin: [name]"
      return `Edit ${t("196")}: ${editingTwin.title}`;
    }

    const { oldestDate, newestDate } = calculateDateRange(selectedItems);

    if (!oldestDate || !newestDate) {
      // add translation for "New Digital Twin"
      return t("194");
    }

    return (
      // add translation for "Digital Twin"
      t("196") +
      " " +
      getTwinName({
        oldest_date: oldestDate,
        newest_date: newestDate,
      })
    );
  };

  const getTwinName = ({ oldest_date, newest_date }) => {
    try {
      const oldestDate = formatDateAsMonthYear(oldest_date);
      const newestDate = formatDateAsMonthYear(newest_date);

      if (
        !oldestDate ||
        !newestDate ||
        oldestDate === "INVALID DATE" ||
        newestDate === "INVALID DATE"
      ) {
        // add translation for "Unknown Date Range"
        return t("270");
      }

      if (oldestDate === newestDate) {
        return `${oldestDate}`;
      }

      // If the last 2 digits of the year are the same, return only the month
      if (oldestDate.slice(-2) === newestDate.slice(-2)) {
        return `${oldestDate.slice(0, -2)}-${newestDate}`;
      }

      return `${oldestDate}-${newestDate}`;
    } catch (error) {
      // add translation for "Invalid Date Range"
      return t("271");
    }
  };

  const handleConfirmClick = async () => {
    try {
      if (isEditMode && editingTwin) {
        // EDIT MODE: Construct PATCH payload with only new/changed fields
        const updatePayload = {};

        // Process DX items
        const dxItems = selectedItems.DX || [];
        const lockedDxIds = lockedSelections.DX || [];

        dxItems.forEach(item => {
          // Only add if not locked (i.e., it's new)
          if (!lockedDxIds.includes(item._id)) {
            const orientation = item.orientation?.toLowerCase() || "";
            if (
              orientation.includes(InstanceOrientation.Coronal) &&
              !editingTwin.coronal_dx_instance_id
            ) {
              updatePayload.coronal_dx_instance_id = item._id;
            } else if (
              orientation.includes(InstanceOrientation.Sagittal) &&
              !editingTwin.sagittal_dx_instance_id
            ) {
              updatePayload.sagittal_dx_instance_id = item._id;
            }
          }
        });

        // Process CT item (only if slot is empty)
        const ctItem =
          selectedItems.CT && selectedItems.CT.length > 0
            ? selectedItems.CT[0]
            : null;
        const lockedCtIds = lockedSelections.CT || [];
        if (ctItem && !lockedCtIds.includes(ctItem._id)) {
          if (!editingTwin.ct_instance_id) {
            updatePayload.ct_instance_id = ctItem._id;
          }
        }

        // Process MR item (only if slot is empty)
        const mriItem =
          selectedItems.MR && selectedItems.MR.length > 0
            ? selectedItems.MR[0]
            : null;
        const lockedMrIds = lockedSelections.MR || [];
        if (mriItem && !lockedMrIds.includes(mriItem._id)) {
          if (!editingTwin.mri_instance_id) {
            updatePayload.mri_instance_id = mriItem._id;
          }
        }

        // Process posture images (only fill empty orientation slots)
        const postureItems = selectedItems.Posture || [];
        const lockedPostureUrls = lockedSelections.Posture || [];

        postureItems.forEach(item => {
          if (!lockedPostureUrls.includes(item.url)) {
            const orientation = (
              item.orientation ||
              item.position ||
              ""
            ).toLowerCase();

            if (
              orientation.includes(MorphologyOrientation.FRONT.toLowerCase()) &&
              !editingTwin.front_morphology_picture
            ) {
              updatePayload.front_morphology_picture = item.title;
            } else if (
              orientation.includes(MorphologyOrientation.BACK.toLowerCase()) &&
              !editingTwin.back_morphology_picture
            ) {
              updatePayload.back_morphology_picture = item.title;
            } else if (
              orientation.includes(MorphologyOrientation.LEFT.toLowerCase()) &&
              !editingTwin.left_morphology_picture
            ) {
              updatePayload.left_morphology_picture = item.title;
            } else if (
              orientation.includes(MorphologyOrientation.RIGHT.toLowerCase()) &&
              !editingTwin.right_morphology_picture
            ) {
              updatePayload.right_morphology_picture = item.title;
            }
          }
        });

        // Process pain assessment (only if slot is empty)
        const painItem =
          selectedItems[SPINESIM_MODALITIES.PAIN]?.length > 0
            ? selectedItems[SPINESIM_MODALITIES.PAIN][0]
            : null;
        const lockedPainIds = lockedSelections[SPINESIM_MODALITIES.PAIN] || [];
        if (painItem && !lockedPainIds.includes(painItem._id)) {
          if (!editingTwin.pain_assessment) {
            updatePayload.pain_assessment = painItem._id;
          }
        }

        // Process clinical history (only if slot is empty)
        const clinicalHistoryItem =
          selectedItems[SPINESIM_MODALITIES.CLINICAL_HISTORY]?.length > 0
            ? selectedItems[SPINESIM_MODALITIES.CLINICAL_HISTORY][0]
            : null;
        const lockedClinicalHistoryIds =
          lockedSelections[SPINESIM_MODALITIES.CLINICAL_HISTORY] || [];
        if (
          clinicalHistoryItem &&
          !lockedClinicalHistoryIds.includes(clinicalHistoryItem._id)
        ) {
          if (!editingTwin.clinical_history_id) {
            updatePayload.clinical_history_id = clinicalHistoryItem._id;
          }
        }

        // Recalculate date range if we have new data
        if (Object.keys(updatePayload).length > 0) {
          const { oldestDate, newestDate } = calculateDateRange(selectedItems);

          if (oldestDate && newestDate) {
            // Update dates if the new data extends the range
            const currentOldest = editingTwin.date_of_oldest_file;
            const currentNewest = editingTwin.date_of_newest_file;

            if (oldestDate < currentOldest) {
              updatePayload.date_of_oldest_file = oldestDate;
            }
            if (newestDate > currentNewest) {
              updatePayload.date_of_newest_file = newestDate;
            }

            // Update title if date range changed
            if (
              updatePayload.date_of_oldest_file ||
              updatePayload.date_of_newest_file
            ) {
              const finalOldest =
                updatePayload.date_of_oldest_file || currentOldest;
              const finalNewest =
                updatePayload.date_of_newest_file || currentNewest;
              updatePayload.title = getTwinName({
                oldest_date: finalOldest,
                newest_date: finalNewest,
              });
            }
          }

          // Call updateDigitalTwin with the update payload and twin ID (note: data first, then ID)
          await updateDigitalTwin(updatePayload, editingTwin.id);
          onGenerateTwin(editingTwin.id);
        }
      } else {
        // CREATION MODE: Original logic
        // Use the extracted date range calculation function
        const { oldestDate, newestDate } = calculateDateRange(selectedItems);

        if (!oldestDate || !newestDate) {
          return;
        }

        const dxItems = selectedItems.DX || [];
        const ctItem =
          selectedItems.CT && selectedItems.CT.length > 0
            ? selectedItems.CT[0]
            : null;
        const mriItem =
          selectedItems.MR && selectedItems.MR.length > 0
            ? selectedItems.MR[0]
            : null;

        // Find DX items by orientation
        let coronalDxItem = null;
        let sagittalDxItem = null;

        dxItems.forEach(item => {
          const orientation = item.orientation?.toLowerCase() || "";
          if (orientation.includes(InstanceOrientation.Coronal)) {
            coronalDxItem = item;
          } else if (orientation.includes(InstanceOrientation.Sagittal)) {
            sagittalDxItem = item;
          }
        });

        // Find posture images by orientation
        const postureItems = selectedItems.Posture || [];
        let frontPostureItem = null;
        let backPostureItem = null;
        let leftPostureItem = null;
        let rightPostureItem = null;

        postureItems.forEach(item => {
          const orientation = (
            item.orientation ||
            item.position ||
            ""
          ).toLowerCase();
          if (orientation.includes(MorphologyOrientation.FRONT.toLowerCase())) {
            frontPostureItem = item;
          } else if (
            orientation.includes(MorphologyOrientation.BACK.toLowerCase())
          ) {
            backPostureItem = item;
          } else if (
            orientation.includes(MorphologyOrientation.LEFT.toLowerCase())
          ) {
            leftPostureItem = item;
          } else if (
            orientation.includes(MorphologyOrientation.RIGHT.toLowerCase())
          ) {
            rightPostureItem = item;
          }
        });

        const painItem =
          selectedItems[SPINESIM_MODALITIES.PAIN]?.length > 0
            ? selectedItems[SPINESIM_MODALITIES.PAIN][0]
            : null;

        const clinicalHistoryItem =
          selectedItems[SPINESIM_MODALITIES.CLINICAL_HISTORY]?.length > 0
            ? selectedItems[SPINESIM_MODALITIES.CLINICAL_HISTORY][0]
            : null;

        // Create the digital twin object with normalized dates
        const newTwin = {
          title: getTwinName({
            oldest_date: oldestDate,
            newest_date: newestDate,
          }),
          date_of_creation: new Date()
            .toISOString()
            .split("T")[0]
            .replace(/-/g, ""),
          date_of_oldest_file: oldestDate,
          date_of_newest_file: newestDate,
          patient_id: params.patientId,
          ct_instance_id: ctItem ? ctItem._id : null,
          mri_instance_id: mriItem ? mriItem._id : null,
          coronal_dx_instance_id: coronalDxItem ? coronalDxItem._id : null,
          sagittal_dx_instance_id: sagittalDxItem ? sagittalDxItem._id : null,
          front_morphology_picture: frontPostureItem
            ? frontPostureItem.title
            : null,
          back_morphology_picture: backPostureItem
            ? backPostureItem.title
            : null,
          right_morphology_picture: rightPostureItem
            ? rightPostureItem.title
            : null,
          left_morphology_picture: leftPostureItem
            ? leftPostureItem.title
            : null,
          pain_assessment: painItem ? painItem._id : null,
          clinical_history_id: clinicalHistoryItem
            ? clinicalHistoryItem._id
            : null,
          key_parameters: userPreferences?.spino_pelvic_parameters || [],
        };

        const newTwinId = await addDigitalTwin(newTwin);
        if (newTwinId) {
          onGenerateTwin(newTwinId);
        }
      }
    } catch (error) {}
  };

  const openAddPainAssessment = () => {
    showPopup(
      <PainAssesmentModal
        onSave={() => {
          // Refresh patient context to get updated pain assessments
          refreshPatientContext();
        }}
      />
    );
  };

  const openAddClinicalHistory = () => {
    showPopup(<ClinicalHistoryModal />);
  };

  const handleImportAreaClick = modality => {
    if (modality === SPINESIM_MODALITIES.PAIN) {
      openAddPainAssessment();
    } else if (modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
      openAddClinicalHistory();
    } else {
      showPopup(
        <FileImport
          onSave={importedFiles => {
            const expectedCounts = importedFiles.reduce(
              (counts, group) => {
                const normalizedModality =
                  group.modality === MODALITY.MO
                    ? SPINESIM_MODALITIES.POSTURE
                    : group.modality === MODALITY.CR
                      ? SPINESIM_MODALITIES.DX
                      : group.modality;

                counts[normalizedModality] =
                  (counts[normalizedModality] || 0) + 1;
                return counts;
              },
              {}
            );

            setPendingImportSelection({
              expectedCounts,
              previousIdsByModality: {
                [SPINESIM_MODALITIES.DX]: new Set(
                  (instancesByModality[SPINESIM_MODALITIES.DX] || []).map(
                    instance => instance._id
                  )
                ),
                [SPINESIM_MODALITIES.CT]: new Set(
                  (instancesByModality[SPINESIM_MODALITIES.CT] || []).map(
                    instance => instance._id
                  )
                ),
                [SPINESIM_MODALITIES.MR]: new Set(
                  (instancesByModality[SPINESIM_MODALITIES.MR] || []).map(
                    instance => instance._id
                  )
                ),
              },
              previousPostureTitles: new Set(
                [...posturalImages, ...posturalImagesNoBg].map(
                  image => image.title
                )
              ),
            });
          }}
          onProcessingComplete={async success => {
            if (!success) {
              setPendingImportSelection(null);
              return;
            }

            await refreshPatientContext();
            await fetchPatientImages();
            await refetch();
          }}
        />
      );
    }
  };
  // endregion

  // Calculate whether requirements are met for the Generate button
  const requirementsMet = validateRequirements();

  return (
    <div className="DigitalTwinDataInput-container">
      <div className="DigitalTwinDataInput-header">
        <IconButton
          icon={IconsEnum.GOBACK}
          noBgColor
          onClick={onGoBack}
          title={t("289")}
        />
        <div className="DigitalTwinDataInput-title App-title-widgets">
          {getDynamicTitle()}
        </div>
      </div>

      <div className="DigitalTwinDataInput-content">
        <div className="DigitalTwinDataInput-wrapper">
          <div className="DigitalTwinDataInput-line">
            {stepData.map((_, idx) => (
              <DigitalTwinDataInputsStepsHandle
                key={idx}
                step={idx}
                isSelected={selectedStep === idx}
                onSelectStep={setSelectedStep}
              />
            ))}
          </div>
          <div className="DigitalTwinDataInput-steps">
            {stepData.map((data, idx) => (
              <DigitalTwinDataInputsSteps
                key={idx}
                step={idx}
                isSelected={selectedStep === idx}
                onSelectStep={setSelectedStep}
                title={data.title}
                subtitle={data.subtitle}
              >
                {data.sections}
              </DigitalTwinDataInputsSteps>
            ))}
          </div>
        </div>
      </div>

      <div className="DigitalTwinDataInput-footer">
        <NavigationButtons
          steps={stepData}
          currentStepIndex={selectedStep}
          onPrevGroup={handlePrevStep}
          onNextGroup={handleNextStep}
          onConfirmClick={handleConfirmClick}
          // add translation for "Generate" button (or "Update Twin" in edit mode)
          confirmButtonText={isEditMode ? "Update Twin" : t("272")}
          isPreviousButtonDisabled={selectedStep === 0}
          isConfirmButtonDisabled={!requirementsMet}
        />
      </div>
    </div>
  );
};

export default DigitalTwinDataInputs;
//endregion
